import { Check, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PricingSection = () => {
  const navigate = useNavigate();
  
  const plans = [
    {
      id: "starter",
      name: "Starter",
      subtitle: "Pour démarrer",
      price: "199",
      setupFee: "200",
      engagement: "3 mois",
      features: [
        { text: "3 flacons", included: true },
        { text: "100ML / flacon / trimestre", included: true },
        { text: "Affichage digital max 50%", included: true },
        { text: "Modification écran tous les 3 mois", included: true },
        { text: "QR code inclus", included: true },
        { text: "Rapport mensuel", included: true },
        { text: "Modification fragrances après 60 jours", included: true },
        { text: "Personnalisation logo", included: false }
      ],
      highlighted: false
    },
    {
      id: "premium",
      name: "Premium",
      subtitle: "Recommandé",
      price: "399",
      setupFee: "200",
      engagement: "3 mois",
      features: [
        { text: "5 flacons", included: true },
        { text: "100ML / flacon / trimestre", included: true },
        { text: "Affichage digital max 70%", included: true },
        { text: "Modification écran tous les 3 mois", included: true },
        { text: "QR code inclus", included: true },
        { text: "Rapport mensuel", included: true },
        { text: "Modification fragrances après 45 jours", included: true },
        { text: "Personnalisation logo", included: false }
      ],
      highlighted: true
    },
    {
      id: "vip",
      name: "VIP",
      subtitle: "Engagement long terme",
      price: "399",
      setupFee: "200",
      engagement: "18 mois",
      features: [
        { text: "5 flacons", included: true },
        { text: "100ML / flacon / trimestre", included: true },
        { text: "Affichage digital max 80%", included: true },
        { text: "Modification écran tous les 2 mois", included: true },
        { text: "QR code inclus", included: true },
        { text: "Rapport mensuel", included: true },
        { text: "Modification fragrances après 30 jours", included: true },
        { text: "Personnalisation logo", included: true, highlight: true }
      ],
      highlighted: false
    }
  ];

  const handleSelectPackage = (packageId) => {
    navigate("/onboarding-parfum", { state: { selectedPackage: packageId } });
  };

  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="bg-[#FAFAFA] py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Label */}
        <p
          data-testid="pricing-label"
          className="font-body text-sm tracking-[0.2em] uppercase text-[#B89F7C] mb-6"
        >
          Tarifs
        </p>

        {/* Main Headline */}
        <h2
          data-testid="pricing-headline"
          className="font-heading text-4xl sm:text-5xl font-light tracking-tight leading-tight text-[#0A0A0A] mb-8 max-w-3xl"
        >
          Investissez dans une visibilité
          <br />
          <span className="italic">qui convertit</span>
        </h2>

        <p className="font-body text-lg font-light leading-relaxed text-[#525252] max-w-2xl mb-16">
          Prix HT par mois. Frais de mise en service : 200€ HT.
        </p>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.id}
              data-testid={`pricing-card-${plan.id}`}
              className={`relative p-10 flex flex-col h-full border transition-colors duration-500 ${
                plan.highlighted
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white border-[#E5E5E5] hover:border-[#B89F7C]"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-10 bg-[#B89F7C] text-white text-xs tracking-widest uppercase px-4 py-1 font-body font-medium">
                  Recommandé
                </div>
              )}

              <p
                className={`font-body text-sm tracking-[0.15em] uppercase mb-2 ${
                  plan.highlighted ? "text-[#B89F7C]" : "text-[#525252]"
                }`}
              >
                {plan.subtitle}
              </p>
              <h3
                className={`font-heading text-2xl mb-4 ${
                  plan.highlighted ? "text-white" : "text-[#0A0A0A]"
                }`}
              >
                {plan.name}
              </h3>

              <div className="mb-4">
                <span
                  className={`font-heading text-5xl font-light ${
                    plan.highlighted ? "text-white" : "text-[#0A0A0A]"
                  }`}
                >
                  {plan.price}€
                </span>
                <span
                  className={`font-body text-sm ml-2 ${
                    plan.highlighted ? "text-white/70" : "text-[#525252]"
                  }`}
                >
                  HT / mois
                </span>
              </div>

              <p
                className={`font-body text-sm mb-6 ${
                  plan.highlighted ? "text-[#B89F7C]" : "text-[#B89F7C]"
                }`}
              >
                Engagement : {plan.engagement}
              </p>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li
                    key={feature.text}
                    className={`flex items-start gap-3 font-body text-sm ${
                      plan.highlighted ? "text-white/90" : "text-[#525252]"
                    }`}
                  >
                    {feature.included ? (
                      <Check
                        size={18}
                        className={
                          feature.highlight
                            ? "text-[#B89F7C]"
                            : plan.highlighted
                            ? "text-[#B89F7C]"
                            : "text-[#0A0A0A]"
                        }
                      />
                    ) : (
                      <Lock
                        size={18}
                        className={plan.highlighted ? "text-white/40" : "text-[#525252]/40"}
                      />
                    )}
                    <span className={!feature.included ? "opacity-50" : feature.highlight ? "font-medium" : ""}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPackage(plan.id)}
                data-testid={`btn-select-${plan.id}`}
                className={`w-full py-4 text-sm tracking-widest uppercase font-medium transition-colors duration-300 ${
                  plan.highlighted
                    ? "bg-white text-[#0A0A0A] hover:bg-[#B89F7C] hover:text-white"
                    : "bg-[#0A0A0A] text-white hover:bg-[#B89F7C]"
                }`}
              >
                Choisir ce forfait
              </button>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="grid md:grid-cols-3 gap-6">
          <div
            data-testid="pricing-info-1"
            className="bg-white border border-[#E5E5E5] p-8"
          >
            <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
              Inclus dans tous les forfaits
            </p>
            <p className="font-heading text-xl text-[#0A0A0A] mb-2">
              QR Code & Rapports
            </p>
            <p className="font-body text-sm text-[#525252]">
              Suivez les performances de vos diffuseurs avec des rapports mensuels détaillés
            </p>
          </div>
          <div
            data-testid="pricing-info-2"
            className="bg-white border border-[#E5E5E5] p-8"
          >
            <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
              Flexibilité
            </p>
            <p className="font-heading text-xl text-[#0A0A0A] mb-2">
              Modification des fragrances
            </p>
            <p className="font-body text-sm text-[#525252]">
              Changez vos parfums après la période de stabilisation (30 à 60 jours selon l'offre)
            </p>
          </div>
          <div
            data-testid="pricing-info-3"
            className="bg-white border border-[#E5E5E5] p-8"
          >
            <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
              VIP Exclusif
            </p>
            <p className="font-heading text-xl text-[#0A0A0A] mb-2">
              Personnalisation logo
            </p>
            <p className="font-body text-sm text-[#525252]">
              Affichez votre marque directement sur les diffuseurs avec l'offre VIP
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
